import { AuthProtocolGuards, ComposedAuthProtocol, } from "./auth-protocol.js";
import { BrowserRegistry } from "../../registry.js";
import { AuthWireService } from "./auth-wire.service.js";
import { getEnvToken, getGsbTenantCode, getTokenFromCookie, setGsbToken, } from "../../config/gsb-config.js";
import { GenericCacheService } from "../cache/generic-cache.service.js";
import { GsbApiService } from "../../api/gsb-api.service.js";
// Storage key prefixes
const TOKEN_PREFIX = "gsb_auth_";
const USER_DATA_PREFIX = "gsb_user_";
const REMEMBER_PREFIX = "gsb_remember_";
const ACTIVE_TENANT_KEY = "gsb_active_tenant";
// Role constants
const ADMIN_ROLE_ID = "59b62a77-b722-44ad-9a2d-478365e8f1a0";
// Cache keys
const CACHE_PREFIX = "auth_";
export const AUTH_STATE_CHANGED_EVENT = "gsb-auth-state-changed";
export const AUTH_USER_UPDATED_EVENT = "gsb-auth-user-updated";
// Token refresh settings
const TOKEN_REFRESH_BUFFER = 3 * 60 * 1000; // 3 minutes before expiry
const TOKEN_REFRESH_RETRY_DELAY = 30 * 1000; // 30 seconds between retries
const TOKEN_REFRESH_MAX_RETRIES = 3;
function getHttpErrorFields(error) {
    if (!(error instanceof Error) || typeof error !== "object")
        return {};
    const status = "status" in error && typeof error.status === "number"
        ? error.status
        : undefined;
    const errorCode = "code" in error && typeof error.code === "string" ? error.code : undefined;
    return {
        ...(errorCode ? { errorCode } : {}),
        ...(status !== undefined ? { status } : {}),
    };
}
// Singleton instance
let authServiceInstance = null;
/**
 * Authentication service for managing auth tokens with GSB backend
 * Single-tenant implementation with forward compatibility for multi-tenant
 */
export class AuthService {
    static coreComposition;
    appliedComposition;
    guards = new AuthProtocolGuards();
    static composeCore(composition) {
        AuthService.coreComposition = composition;
    }
    applyCoreComposition(composition = AuthService.coreComposition) {
        if (!composition || this.appliedComposition === composition)
            return;
        this.gsbService = composition.wire();
        const cache = composition.cache();
        cache.adoptGenericState(this.cacheService);
        this.cacheService = cache;
        this.appliedComposition = composition;
    }
    protocol() {
        const core = this.appliedComposition?.protocol;
        const registered = BrowserRegistry.lookup("validation")?.auth;
        return new ComposedAuthProtocol(new ComposedAuthProtocol(this.guards, core), registered);
    }
    gsbService;
    cacheService;
    token = null;
    tenantCode;
    rememberMe = false;
    refreshTokenTimeout = null;
    refreshTokenRetryCount = 0;
    sessionChannel = null;
    sessionEndReason = null;
    getSessionEndReason() {
        return this.sessionEndReason;
    }
    constructor() {
        this.gsbService =
            AuthService.coreComposition?.wire() ?? new AuthWireService();
        this.cacheService =
            AuthService.coreComposition?.cache() ?? new GenericCacheService();
        this.appliedComposition = AuthService.coreComposition;
        GsbApiService.getInstance().setUnauthorizedHandler(() => this.logout());
        this.tenantCode = getGsbTenantCode();
        this.initFromStorage();
    }
    static getInstance() {
        if (typeof window !== "undefined") {
            const instance = BrowserRegistry.acquire("auth", () => authServiceInstance ?? new AuthService());
            instance.applyCoreComposition(AuthService.coreComposition);
            instance.initializeSessionChannel();
            return instance;
        }
        if (!authServiceInstance) {
            authServiceInstance = new AuthService();
        }
        authServiceInstance.applyCoreComposition();
        authServiceInstance.initializeSessionChannel();
        return authServiceInstance;
    }
    initializeSessionChannel() {
        if (this.sessionChannel ||
            typeof window === "undefined" ||
            typeof BroadcastChannel === "undefined")
            return;
        this.sessionChannel = new BroadcastChannel("gsb-auth-session");
        this.sessionChannel.onmessage = (event) => {
            const notification = this.protocol().notification.safeParse(event.data);
            if (notification.success &&
                (notification.data.allTenants ||
                    notification.data.tenantCode === this.getTenantCode())) {
                this.clearSession(notification.data.allTenants, "remote-signout");
            }
        };
    }
    getKeys(tenantCode, keyType) {
        let prefix;
        let cachePrefix;
        switch (keyType) {
            case "token":
                prefix = TOKEN_PREFIX;
                cachePrefix = `${CACHE_PREFIX}token_`;
                break;
            case "userData":
                prefix = USER_DATA_PREFIX;
                cachePrefix = `${CACHE_PREFIX}userData_`;
                break;
            case "expiry":
                prefix = `${TOKEN_PREFIX}expiry_`;
                cachePrefix = `${CACHE_PREFIX}expiry_`;
                break;
            case "remember":
                prefix = REMEMBER_PREFIX;
                cachePrefix = `${CACHE_PREFIX}remember_`;
                break;
        }
        return {
            storageKey: `${prefix}${tenantCode}`,
            cacheKey: `${cachePrefix}${tenantCode}`,
        };
    }
    getCacheValue(tenantCode, keyType) {
        const { cacheKey, storageKey } = this.getKeys(tenantCode, keyType);
        return this.cacheService.getCache(cacheKey, storageKey);
    }
    setCacheValue(tenantCode, keyType, value, duration) {
        const { cacheKey, storageKey } = this.getKeys(tenantCode, keyType);
        this.cacheService.setCache(cacheKey, value, duration, storageKey);
    }
    removeCacheValue(tenantCode, keyType) {
        const { cacheKey, storageKey } = this.getKeys(tenantCode, keyType);
        this.cacheService.removeCache(cacheKey, storageKey);
    }
    saveToStorage(token, userData, remember) {
        userData = userData ? this.projectUserData(userData) : undefined;
        // Always update memory state
        this.token = token;
        this.sessionEndReason = null;
        this.rememberMe = remember ?? false;
        this.tenantCode = this.getTenantCode();
        if (!this.tenantCode) {
            return;
        }
        if (typeof window !== "undefined") {
            try {
                // Save active tenant
                this.cacheService.setCache("active_tenant", this.tenantCode, undefined, ACTIVE_TENANT_KEY);
                // Calculate cache duration based on token expiry
                let cacheDuration;
                let tokenExpiryTime = null;
                // First, try to get expiry from userData
                if (userData?.expireDate) {
                    tokenExpiryTime = new Date(userData.expireDate).getTime();
                }
                else {
                    // If not in userData, try to decode from JWT token
                    try {
                        const tokenPayload = this.getUserFromToken(token);
                        if (tokenPayload?.exp) {
                            // JWT exp is in seconds, convert to milliseconds
                            tokenExpiryTime = tokenPayload.exp * 1000;
                        }
                    }
                    catch (error) {
                        console.warn("Could not decode token expiry:", error);
                    }
                }
                if (tokenExpiryTime) {
                    const now = Date.now();
                    const tokenDuration = Math.max(0, tokenExpiryTime - now);
                    // Use the token's actual expiry time
                    cacheDuration = tokenDuration;
                }
                else {
                    // Fallback to hardcoded durations only if no expiry found
                    cacheDuration = remember
                        ? 30 * 24 * 60 * 60 * 1000
                        : 24 * 60 * 60 * 1000;
                }
                if (!userData?.expireDate && tokenExpiryTime) {
                    userData = {
                        ...(userData ?? {}),
                        expireDate: new Date(tokenExpiryTime).toISOString(),
                    };
                }
                // Store token and user data in cache
                this.removeCacheValue(this.tenantCode, "token");
                this.setCacheValue(this.tenantCode, "userData", userData, cacheDuration);
                this.setCacheValue(this.tenantCode, "remember", remember ?? false, cacheDuration);
                // Set cookies for middleware to use
                if (typeof document !== "undefined") {
                    let cookieExpiry;
                    if (remember) {
                        if (tokenExpiryTime) {
                            // Use actual token expiry for remember me
                            cookieExpiry = new Date(tokenExpiryTime);
                        }
                        else {
                            // Fallback to cache duration if no token expiry
                            cookieExpiry = new Date(Date.now() + cacheDuration);
                        }
                    }
                    // For session (non-remember), cookieExpiry remains undefined (session cookie)
                    const tokenKeys = this.getKeys(this.tenantCode, "token");
                    document.cookie = `${tokenKeys.storageKey}=${token}; path=/; ${cookieExpiry ? `expires=${cookieExpiry.toUTCString()};` : ""} SameSite=Lax`;
                    // Store token expiry for validation
                    if (userData?.expireDate) {
                        const expiryKeys = this.getKeys(this.tenantCode, "expiry");
                        document.cookie = `${expiryKeys.storageKey}=${userData.expireDate}; path=/; ${cookieExpiry ? `expires=${cookieExpiry.toUTCString()};` : ""} SameSite=Lax`;
                        // Cache expiry date
                        this.setCacheValue(this.tenantCode, "expiry", userData.expireDate, cacheDuration);
                    }
                }
                // Refresh both session and persistent tokens before their JWT expires.
                if (userData?.expireDate) {
                    this.scheduleTokenRefresh(userData.expireDate);
                }
                else {
                    this.clearTokenRefresh();
                }
            }
            catch (error) {
                console.error("Error saving auth data to storage:", error);
            }
        }
    }
    scheduleTokenRefresh(expireDate) {
        // Clear any existing refresh timeout
        this.clearTokenRefresh();
        this.refreshTokenRetryCount = 0;
        // Only setup refresh in browser environment
        if (typeof window !== "undefined") {
            try {
                const expiryTime = new Date(expireDate).getTime();
                const now = Date.now();
                // Calculate time until refresh (3 minutes before expiry)
                const timeUntilRefresh = Math.max(0, expiryTime - now - TOKEN_REFRESH_BUFFER);
                if (timeUntilRefresh > 0) {
                    this.refreshTokenTimeout = setTimeout(() => {
                        this.performTokenRefresh();
                    }, timeUntilRefresh);
                }
                else {
                    // Token is already close to expiring or expired, refresh immediately
                    this.performTokenRefresh();
                }
            }
            catch (error) {
                console.error("Error scheduling token refresh:", error);
            }
        }
    }
    performTokenRefresh() {
        this.refreshToken()
            .then((response) => {
            if (response.success) {
                // Reset retry count on success
                this.refreshTokenRetryCount = 0;
                console.warn("Token refreshed successfully");
            }
            else {
                this.handleRefreshFailure();
            }
        })
            .catch(() => {
            this.handleRefreshFailure();
        });
    }
    handleRefreshFailure() {
        this.refreshTokenRetryCount++;
        if (this.refreshTokenRetryCount <= TOKEN_REFRESH_MAX_RETRIES) {
            console.warn(`Token refresh failed. Retrying (${this.refreshTokenRetryCount}/${TOKEN_REFRESH_MAX_RETRIES}) in ${TOKEN_REFRESH_RETRY_DELAY / 1000}s`);
            // Schedule retry
            this.refreshTokenTimeout = setTimeout(() => {
                this.performTokenRefresh();
            }, TOKEN_REFRESH_RETRY_DELAY);
        }
        else {
            console.error("Token refresh failed after maximum retry attempts");
            // Reset retry count for next time
            this.refreshTokenRetryCount = 0;
        }
    }
    clearTokenRefresh() {
        if (this.refreshTokenTimeout) {
            clearTimeout(this.refreshTokenTimeout);
            this.refreshTokenTimeout = null;
        }
    }
    clearStorage(allTenants = false) {
        // Clear token refresh
        this.clearTokenRefresh();
        if (typeof window !== "undefined") {
            try {
                if (allTenants) {
                    // Get all available tenants
                    const availableTenants = this.getAvailableTenants();
                    // Clear data for each tenant
                    availableTenants.forEach((tenant) => {
                        // Remove from cache
                        this.removeCacheValue(tenant, "token");
                        this.removeCacheValue(tenant, "userData");
                        this.removeCacheValue(tenant, "expiry");
                        this.removeCacheValue(tenant, "remember");
                        // Clear cookies
                        if (typeof document !== "undefined") {
                            const tokenKeys = this.getKeys(tenant, "token");
                            const expiryKeys = this.getKeys(tenant, "expiry");
                            document.cookie = `${tokenKeys.storageKey}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
                            document.cookie = `${expiryKeys.storageKey}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
                        }
                    });
                    // Remove active tenant
                    this.cacheService.removeCache("active_tenant", ACTIVE_TENANT_KEY);
                }
                else {
                    const tenantCode = this.getTenantCode();
                    if (!tenantCode) {
                        return;
                    }
                    // Clear only current tenant data
                    this.removeCacheValue(tenantCode, "token");
                    this.removeCacheValue(tenantCode, "userData");
                    this.removeCacheValue(tenantCode, "expiry");
                    this.removeCacheValue(tenantCode, "remember");
                    // Clear cookies
                    if (typeof document !== "undefined") {
                        const tokenKeys = this.getKeys(tenantCode, "token");
                        const expiryKeys = this.getKeys(tenantCode, "expiry");
                        document.cookie = `${tokenKeys.storageKey}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
                        document.cookie = `${expiryKeys.storageKey}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
                    }
                }
            }
            catch (error) {
                console.error("Error clearing storage:", error);
            }
        }
    }
    /**
     * Initialize auth state from storage if available
     */
    initFromStorage() {
        this.tenantCode = this.getTenantCode();
        if (!this.tenantCode) {
            return;
        }
        if (typeof window !== "undefined") {
            try {
                // Get the active tenant from cache or use the current tenant code
                const activeTenant = this.cacheService.getCache("active_tenant", ACTIVE_TENANT_KEY) || this.tenantCode;
                // If tenant changed, update internal state
                if (activeTenant !== this.tenantCode) {
                    this.tenantCode = activeTenant;
                }
                // Try to get token from cache
                this.removeCacheValue(this.tenantCode, "token");
                const token = getTokenFromCookie(this.tenantCode);
                const userData = this.getCurrentUser();
                if (token) {
                    this.token = token;
                    this.rememberMe =
                        this.getCacheValue(this.tenantCode, "remember") ?? false;
                    if (userData?.expireDate) {
                        this.scheduleTokenRefresh(userData.expireDate);
                    }
                }
            }
            catch (error) {
                console.error("Error initializing from storage:", error);
            }
        }
    }
    /**
     * Refresh the authentication token
     * @returns Promise with the refresh response
     */
    async refreshToken() {
        try {
            if (!this.token) {
                return {
                    success: false,
                    error: "No token to refresh",
                };
            }
            const response = await this.gsbService.refreshToken({}, this.token, this.tenantCode);
            if (this.protocol().mfa(response)) {
                this.logout();
                return {
                    success: false,
                    error: "Email verification is required. Please sign in again.",
                };
            }
            if (response?.auth?.token) {
                const token = response.auth.token;
                // Get current user data
                const userData = this.getCurrentUser() || {};
                // Update expiry date if provided
                if (response.auth.expireDate) {
                    userData.expireDate = response.auth.expireDate;
                }
                else {
                    const tokenPayload = this.getUserFromToken(token);
                    if (tokenPayload?.exp) {
                        userData.expireDate = new Date(tokenPayload.exp * 1000).toISOString();
                    }
                }
                // Preserve the original persistence mode when replacing the token.
                this.setToken(token, userData, this.rememberMe);
                return {
                    success: true,
                    token,
                    userData,
                };
            }
            else {
                return {
                    success: false,
                    error: "Token refresh failed: Invalid response",
                };
            }
        }
        catch (error) {
            console.error("Token refresh error:", error);
            return {
                success: false,
                error: error instanceof Error ? error.message : "Token refresh failed",
            };
        }
    }
    /**
     * Check if user is authenticated
     */
    isAuthenticated() {
        const token = this.getToken();
        if (!token) {
            return false;
        }
        try {
            const userData = this.getCurrentUser();
            if (!userData || !userData.expireDate) {
                return false;
            }
            const expiryDate = new Date(userData.expireDate);
            const now = new Date();
            return expiryDate > now;
        }
        catch (error) {
            console.error("Error checking token expiry:", error);
            return false;
        }
    }
    /**
     * Authenticate a user with GSB
     */
    async login(credentials) {
        try {
            if (credentials.remember === undefined) {
                credentials.remember = true;
            }
            credentials.includeUserInfo = true;
            if (!credentials?.variation?.tenantCode) {
                if (!credentials?.variation) {
                    credentials.variation = {};
                }
                credentials.variation.tenantCode = this.tenantCode;
            }
            const response = await this.gsbService.getToken({
                ...credentials,
                runMfaIfEnabled: true,
                variation: {
                    ...credentials.variation,
                    tenantCode: credentials.variation?.tenantCode ?? this.tenantCode ?? "",
                },
            });
            // Never adopt a token attached to an MFA challenge.
            const challenge = this.protocol().mfa(response);
            if (challenge) {
                return { success: false, ...challenge };
            }
            if (response?.auth?.token) {
                const token = response.auth.token;
                const userData = this.projectUserData({
                    ...response.auth,
                    roles: response.auth.roles || [],
                    groups: response.auth.groups || [],
                });
                this.saveToStorage(token, userData, credentials.remember);
                return {
                    success: true,
                    token,
                    userData,
                };
            }
            else {
                return {
                    success: false,
                    error: "Authentication failed: Invalid token response",
                };
            }
        }
        catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : "Authentication failed",
                ...getHttpErrorFields(error),
            };
        }
    }
    /**
     * Log out the current user
     * @param allTenants Whether to logout from all tenants (default: false)
     */
    logout(allTenants = false) {
        const tenantCode = this.getTenantCode();
        if (tenantCode) {
            this.sessionChannel?.postMessage({
                type: "logout",
                tenantCode,
                allTenants,
            });
        }
        this.clearSession(allTenants);
    }
    clearSession(allTenants, reason) {
        this.sessionEndReason = reason ?? null;
        this.token = null;
        this.clearTokenRefresh();
        this.clearStorage(allTenants);
        void this.cacheService.clearCache();
        setGsbToken(undefined);
        if (typeof globalThis.dispatchEvent === "function") {
            globalThis.dispatchEvent(new CustomEvent(AUTH_STATE_CHANGED_EVENT, { detail: reason }));
        }
    }
    /**
     * Set working tenant for multi-tenant support
     * @param tenantCode The tenant code to switch to
     * @param token Optional JWT token for the tenant (if not provided, attempts to load from storage)
     * @returns True if successful switch, false if failed (e.g., no token available)
     */
    setWorkingTenant(tenantCode, token) {
        // Clear existing refresh for previous tenant
        this.clearTokenRefresh();
        if (tenantCode === this.tenantCode && this.token) {
            // Already on this tenant with valid token
            return true;
        }
        // If token is provided, use it
        if (token) {
            this.tenantCode = tenantCode;
            this.token = token;
            // Save active tenant
            this.cacheService.setCache("active_tenant", tenantCode, undefined, ACTIVE_TENANT_KEY);
            // Cache the token
            this.saveToStorage(token, this.getCacheValue(tenantCode, "userData") ?? undefined, this.getCacheValue(tenantCode, "remember") ?? false);
            // Check if we need to setup token refresh for this tenant
            const userData = this.getCacheValue(tenantCode, "userData");
            if (userData && userData.expireDate && this.token) {
                // If expiry is less than 30 days, it's a session token (not remember me)
                const expiry = new Date(userData.expireDate).getTime();
                const now = Date.now();
                if (expiry - now < 30 * 24 * 60 * 60 * 1000) {
                    this.scheduleTokenRefresh(userData.expireDate);
                }
            }
            return true;
        }
        // Try to get from cache
        this.removeCacheValue(tenantCode, "token");
        const cachedToken = getTokenFromCookie(tenantCode);
        if (cachedToken) {
            this.tenantCode = tenantCode;
            this.token = cachedToken;
            // Save active tenant
            this.cacheService.setCache("active_tenant", tenantCode, undefined, ACTIVE_TENANT_KEY);
            // Check if we need to setup token refresh for this tenant
            const userData = this.getCacheValue(tenantCode, "userData");
            if (userData && userData.expireDate && this.token) {
                // If expiry is less than 30 days, it's a session token (not remember me)
                const expiry = new Date(userData.expireDate).getTime();
                const now = Date.now();
                if (expiry - now < 30 * 24 * 60 * 60 * 1000) {
                    this.scheduleTokenRefresh(userData.expireDate);
                }
            }
            return true;
        }
        // No token available for this tenant
        return false;
    }
    /**
     * Get all available tenant codes from storage
     * @returns Array of tenant codes that have stored tokens
     */
    getAvailableTenants() {
        const tenantCode = this.getTenantCode();
        const availableTenants = new Set();
        if (tenantCode) {
            availableTenants.add(tenantCode);
        }
        return Array.from(availableTenants);
    }
    /**
     * Get user information from JWT token
     */
    getUserFromToken(token) {
        try {
            const [, base64Url] = token.split(".");
            if (!base64Url) {
                return null;
            }
            const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
            const jsonPayload = decodeURIComponent(atob(base64)
                .split("")
                .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                .join(""));
            const payload = JSON.parse(jsonPayload);
            const parsed = this.protocol().jwt.safeParse(payload);
            return parsed.success ? parsed.data : null;
        }
        catch (error) {
            console.error("Error decoding token:", error);
            return null;
        }
    }
    /**
     * Check if the current user has a specific role
     */
    hasRole(role) {
        if (!this.token) {
            return false;
        }
        const userData = this.getCurrentUser();
        if (!userData || !userData.roles) {
            return false;
        }
        return userData.roles.includes(role);
    }
    /**
     * Check if the current user has the admin role
     */
    isAdmin() {
        return this.hasRole(ADMIN_ROLE_ID);
    }
    /**
     * Get the subscription tier from the current user data
     */
    getSubscription() {
        const userData = this.getCurrentUser();
        return userData?.subscription;
    }
    /**
     * Get the current tenant code
     */
    getTenantCode() {
        if (!this.tenantCode) {
            this.tenantCode = getGsbTenantCode();
            if (this.tenantCode) {
                this.cacheService.setCache("tenantCode", this.tenantCode, undefined, this.tenantCode);
            }
        }
        return this.tenantCode;
    }
    /**
     * Get the current auth token
     */
    getToken() {
        const tenantCode = this.getTenantCode();
        if (!tenantCode) {
            return null;
        }
        if (this.token) {
            return this.token;
        }
        this.token = getEnvToken() || null;
        if (this.token) {
            return this.token;
        }
        // If token not loaded yet, try to load from storage
        if (!this.token && typeof window !== "undefined") {
            // Try cache
            this.removeCacheValue(tenantCode, "token");
            const cachedToken = getTokenFromCookie(tenantCode);
            if (cachedToken) {
                this.token = cachedToken;
            }
        }
        return this.token;
    }
    setToken(token, userData, remember = true) {
        this.token = token;
        this.saveToStorage(token, userData ?? this.getCurrentUser() ?? {}, remember);
        setGsbToken(token);
    }
    async sendResetPasswordEmail(email, options = {}) {
        const resp = await this.gsbService.runWfFunction({
            function: {
                name: "sendResetPasswordEmail",
            },
            instance: {
                prms: {
                    email,
                    ...options,
                },
                entity: { email },
            },
        }, this.token ?? "", this.tenantCode ?? "");
        const candidate = typeof resp === "object" &&
            resp !== null &&
            "response" in resp &&
            resp.response !== undefined
            ? resp.response
            : resp;
        return this.protocol().reset(candidate);
    }
    async changePassword(oldPassword, newPassword, email, resetToken) {
        const response = await this.gsbService.changePassword({
            oldPassword,
            newPassword,
            resetToken,
            email,
        }, this.token ?? "", this.tenantCode ?? "");
        return this.protocol().response(response);
    }
    /**
     * Merge saved profile edits into the cached session user. The token, its expiry and the
     * persistence mode are untouched; listeners are told through AUTH_USER_UPDATED_EVENT.
     */
    updateCurrentUser(patch) {
        const token = this.getToken();
        const current = this.getCurrentUser();
        if (!token || !current)
            return null;
        const updated = {
            ...current,
            ...patch,
            ...(patch.name !== undefined ? { surname: undefined } : {}),
        };
        this.saveToStorage(token, updated, this.rememberMe);
        if (typeof globalThis.dispatchEvent === "function") {
            globalThis.dispatchEvent(new Event(AUTH_USER_UPDATED_EVENT));
        }
        return updated;
    }
    /**
     * Get the current user data from storage
     */
    getCurrentUser() {
        if (typeof window === "undefined") {
            return null;
        }
        if (!this.tenantCode) {
            this.tenantCode = getGsbTenantCode();
        }
        if (!this.tenantCode) {
            return null;
        }
        try {
            // Get from cache
            const cached = this.getCacheValue(this.tenantCode, "userData");
            if (!cached)
                return null;
            const userData = this.projectUserData(cached);
            if ("token" in cached) {
                const expiry = userData.expireDate
                    ? Math.max(0, new Date(userData.expireDate).getTime() - Date.now())
                    : undefined;
                this.setCacheValue(this.tenantCode, "userData", userData, expiry);
            }
            return userData;
        }
        catch (error) {
            console.error("Error getting user data from storage:", error);
            return null;
        }
    }
    projectUserData(userData) {
        const { userId, name, surname, email, roles, groups, expireDate, title, subscription, pictureUrl, picture, } = userData;
        return {
            userId,
            name,
            surname,
            email,
            roles,
            groups,
            expireDate,
            title,
            subscription,
            pictureUrl,
            picture,
        };
    }
}
//# sourceMappingURL=auth.service.js.map